<script setup lang="ts">
import type { NewsEntry } from '@kcalc/lib/browser';
import BaseModal from '@/components/BaseModal.vue';

interface Props {
  newsEntry: NewsEntry;
}

defineProps<Props>();
</script>

<template>
  <BaseModal
    class="news-entry-modal"
    size="lg"
    :translucent-header="!!newsEntry.image"
    :title="newsEntry.image ? undefined : newsEntry.title">
    <template #default>
      <div
        v-if="newsEntry.image"
        class="image-container">
        <img
          :src="newsEntry.image.src"
          :alt="newsEntry.title">

        <div
          class="image-bg"
          :style="{ backgroundImage: `url(${newsEntry.image.src})` }" />
      </div>

      <h1>
        {{ newsEntry.title }}
      </h1>

      <div
        v-html="newsEntry.content"
        class="html-content" />
    </template>

    <template
      #actions
      v-if="newsEntry.backlink">
      <a
        :href="newsEntry.backlink"
        target="_blank"
        rel="noopener noreferrer"
        class="btn btn-primary-outline">
        {{ $t('DashboardView.news.readMore') }}
      </a>
    </template>
  </BaseModal>
</template>

<style>
.news-entry-modal {
  --image-height: 320px;

  .image-container {
    position: relative;
    margin: calc(-1 * var(--space-3)) calc(-1 * var(--space-3)) auto;
    display: flex;
    justify-content: center;
    overflow: hidden;

    img {
      max-height: var(--image-height);
      display: block;
      position: relative;
      z-index: 9999;
    }

    .image-bg {
      --blur: 10px;
      position: absolute;
      top: -10px;
      left: -10px;
      width: calc(100% + 20px);
      height: calc(100% + 20px);
      background-size: cover;
      background-position: 50%;
      background-repeat: no-repeat;
      filter: blur(var(--blur));
      -webkit-filter: blur(var(--blur));
      -moz-filter: blur(var(--blur));
      -o-filter: blur(var(--blur));
      -ms-filter: blur(var(--blur));
      z-index: 9998;
    }
  }

  h1 {
    margin: var(--space-3) 0;
  }

  .html-content {
    line-height: var(--line-height-s);

    p, ul, ol {
      &:not(:first-child) {
        margin-top: var(--space-3);
      }
    }

    ul, ol {
      padding-left: var(--space-5);
    }

    a {
      color: var(--color-primary);
      transition: color var(--transition-duration-default) var(--transition-animation-default);

      &:hover {
        color: var(--color-primary-shade);
      }
    }
  }
}
</style>
